<template>
  <div>
    <rule-header
      :rule="mutableRule"
      :product-counts="productCounts || []"
      :class="{ 'expanded-top': isExpanded(rulesetType, ruleIndex) }"
      :expanded="isExpanded(rulesetType, ruleIndex)"
      :disabled="disabled"
      @expand="toggleExpanded(rulesetType, ruleIndex)"
      @deleteRule="deleteRule"
      @copyRule="handleCopyRule"
      @updateDescription="updateDescription"
    />
    <v-expand-transition>
      <v-flex v-if="isExpanded(rulesetType, ruleIndex)" grow>
        <v-card
          outlined
          :class="{ 'elevation-6': !isExpanded(rulesetType, ruleIndex) }"
          class="rule-card expanded-bottom"
        >
          <v-card flat class="rule-content">
            <attribute-filter-panel
              :ruleset-type="rulesetType"
              :filter-rules="mutableRule.scope"
              :enable-non-attribute-filters="false"
              :enable-hierarchy-filters="true"
              :enable-store-group-chain-filter="false"
              :enable-client-product-key-filter="false"
              :hierarchy-filter-level="hierarchyFilterLevel"
              title-localisation="settings.penaltyRules.scope.scopeApplied"
              filter-count-localisation="settings.penaltyRules.scope.numApplied"
              @attributeFilterChange="updateRuleScope"
            />
            <v-tabs
              v-model="tab"
              active-class="pricing-tabs__tab--active"
              background-color="transparent"
              class="pricing-tabs"
              color="white"
              height="4rem"
              hide-slider
            >
              <template v-for="(item, i) in tabs">
                <div
                  :key="item.iconText + i"
                  class="d-flex"
                  :custom-title-msg="item.tooltipMessage"
                  :custom-title-hidden="!item.tooltipMessage"
                  custom-title-position="right"
                >
                  <v-tab
                    class="pricing-tabs__tab pricing-tabs__tab--first pr-0"
                    @click="tabChange(item)"
                  >
                    <v-layout>
                      <v-layout align-self-center justify-start class="pr-1">
                        <v-avatar size="2.5rem" class="traffic-light" :class="item.iconClass">
                          <span class="white--text headline">{{ item.iconText }}</span>
                        </v-avatar>
                        <span class="tab-title">{{ item.tabText }}</span>
                      </v-layout>
                      <v-layout align-self-center justify-end>
                        <v-icon v-if="hasActiveExactMatch(item)" class="pl-2 pr-0">
                          $puzzleCheckmark
                        </v-icon>
                        <v-icon v-if="hasActiveMaxUpDown(item)" class="pl-2 pr-0">
                          mdi-arrow-up-down
                        </v-icon>
                        <v-checkbox
                          v-model="item.status"
                          class="activate-checkbox pl-1 pr-0"
                          :true-value="active"
                          :false-value="inactive"
                          :disabled="isTabDisabled(item)"
                          @change="setStatus(item)"
                        />
                      </v-layout>
                    </v-layout>
                  </v-tab>
                </div>
              </template>
              <div class="pricing-tabs__filler" />
            </v-tabs>
            <v-tabs-items v-model="tab">
              <v-tab-item
                v-for="(item, i) in tabs"
                :key="item.iconText + i"
                class="tab-content"
                :class="{ opaque: item.disabled }"
              >
                <v-container v-if="item.penalty === penaltyCategories.competitor" fluid>
                  <v-row>
                    <v-col v-if="isFixedCompetitorPenaltyWeightEnabled(item)" cols="3">
                      <v-switch
                        v-model="mutableRule.competitorPenalty.weighted"
                        :label="$t('settings.penaltyRules.enableFixedCompetitorPenaltyWeight')"
                        primary
                        dense
                        hide-details
                        @change="toggleFixedCompetitorPenaltyWeight"
                      />
                    </v-col>
                    <v-col cols="9" class="d-flex">
                      <div class="field-heading pr-3 pt-2">
                        {{ $t('settings.penaltyRules.competitorSelectText') }}:
                      </div>
                      <div class="pr-2">
                        <v-select
                          v-model="selectedCompetitorIndex"
                          class="pa-0 ma-0"
                          :items="competitorDropdownValues"
                        >
                          <template v-slot:item="props">
                            <v-layout>
                              <v-layout align-self-center justify-start>
                                <span class="item-text">{{ props.item.text }}</span>
                              </v-layout>
                              <v-layout align-self-center justify-end>
                                <v-icon v-if="isCompetitorActive(props.item.value)" right>
                                  done
                                </v-icon>
                              </v-layout>
                            </v-layout>
                          </template>
                        </v-select>
                      </div>
                      <div>
                        <v-checkbox
                          v-model="competitorIsActive"
                          class="pa-0 ma-0"
                          :label="$t('settings.penaltyRules.active')"
                          :disabled="isTabDisabled(item)"
                          @change="onCompetitorActiveToggle(item)"
                        />
                      </div>
                      <!-- Competitor Exact Match Checkbox -->
                      <div>
                        <v-checkbox
                          v-if="displayCompetitorExactMatch"
                          v-model="competitorIsExactMatch"
                          class="pl-5 pt-0 pr-0 pb-0 ma-0"
                          :label="$t('settings.penaltyRules.exactMatch')"
                          :disabled="isTabDisabled(item)"
                          @change="toggleCompetitorExactMatch"
                        />
                      </div>
                    </v-col>
                  </v-row>
                </v-container>

                <v-container v-if="item.penalty === penaltyCategories.maxUpDown" fluid>
                  <v-row class="mud-row" align="center" no-gutters>
                    <v-col cols="12" md="auto" class="mud-input d-flex align-center">
                      <v-text-field
                        v-model.number="maxUpPctUi"
                        flat
                        :background-color="owWhite"
                        :label="$t('settings.penaltyRules.maxUpPct')"
                        type="number"
                        step="1"
                        inputmode="decimal"
                        :rules="[rules.valueIsPositiveOrZero]"
                        :disabled="isTabDisabled(item) || !applyMaxUp"
                        class="max-up-down-input"
                        suffix="%"
                      />
                    </v-col>
                    <v-col cols="12" md="auto" class="mud-control d-flex align-center">
                      <v-checkbox
                        v-model="applyMaxUp"
                        class="pa-0 ma-0"
                        :label="$t('settings.penaltyRules.Active')"
                        :disabled="isTabDisabled(item)"
                      />
                    </v-col>
                  </v-row>
                  <v-row class="mud-row" align="center" no-gutters>
                    <v-col cols="12" md="auto" class="mud-input d-flex align-center">
                      <v-text-field
                        v-model.number="maxDownPctUi"
                        flat
                        :background-color="owWhite"
                        :label="$t('settings.penaltyRules.maxDownPct')"
                        type="number"
                        step="1"
                        inputmode="decimal"
                        :rules="[rules.valueIsPositiveOrZero]"
                        :disabled="isTabDisabled(item) || !applyMaxDown"
                        class="max-up-down-input"
                        suffix="%"
                      />
                    </v-col>
                    <v-col cols="12" md="auto" class="mud-control d-flex align-center">
                      <v-checkbox
                        v-model="applyMaxDown"
                        class="pa-0 ma-0"
                        :label="$t('settings.penaltyRules.Active')"
                        :disabled="isTabDisabled(item)"
                      />
                    </v-col>
                  </v-row>
                </v-container>

                <store-group-penalty-tab
                  :item="item"
                  :mutable-rule="mutableRule"
                  :disabled="disabled"
                  :store-group-dropdown-values="storeGroupDropdownValues"
                  :selected-store-group-index="selectedStoreGroupIndex"
                  @update:selectedStoreGroupIndex="selectedStoreGroupIndex = $event"
                />

                <v-container v-if="item.penalty === penaltyCategories.minMax" fluid>
                  <v-row>
                    <div class="field-heading pr-3 pt-2">
                      {{ $t('settings.penaltyRules.storeGroupSelectText') }}:
                    </div>
                    <div class="pr-2">
                      <v-select
                        v-model="selectedMinMaxStoreGroupIndex"
                        class="pa-0 ma-0"
                        :items="allStoreGroupDropdownValues"
                      >
                        <template v-slot:item="props">
                          <v-layout>
                            <v-layout align-self-center justify-start>
                              <span class="item-text">{{ props.item.text }}</span>
                            </v-layout>
                            <v-layout align-self-center justify-end>
                              <v-icon v-if="isMinMaxStoreGroupActive(props.item.value)" right>
                                done
                              </v-icon>
                            </v-layout>
                          </v-layout>
                        </template>
                      </v-select>
                    </div>
                    <div>
                      <v-checkbox
                        v-model="minMaxStoreGroupIsActive"
                        class="pa-0 ma-0"
                        :label="$t('settings.penaltyRules.active')"
                        :disabled="isTabDisabled(item)"
                      />
                    </div>
                  </v-row>
                </v-container>

                <v-container v-if="item.penalty === penaltyCategories.pricePointing" fluid>
                  <div class="d-flex flex-column">
                    <span v-if="duplicateRangeMaximumsInPricePointing" class="validation-fail pl-2">
                      {{
                        $t(
                          'settings.penaltyRules.pricePointing.pricePointingRangeMaximumValidationError'
                        )
                      }}
                    </span>
                    <span v-if="missingEmptyPricePointingRange" class="validation-fail pl-2">
                      {{ $t('settings.penaltyRules.pricePointing.pricePointingMissingMaxRange') }}
                    </span>
                    <span v-if="missingTokensForPricePointingRange" class="validation-fail pl-2">
                      {{ $t('settings.penaltyRules.pricePointing.pricePointingMissingOffsets') }}
                    </span>
                  </div>
                  <price-pointing
                    v-for="(allowedValue, index) in item.rule.allowedValues"
                    :key="index"
                    :price-pointing="allowedValue"
                    :index-in-array="index"
                    :disabled="isTabDisabled(item)"
                    @deletePricePointing="deletePricePointing"
                    @updateRangeMaximum="updateRangeMaximum"
                    @updateOffset="updateOffset"
                    @updateBase="updateBase"
                  />
                  <div v-if="!disabled" class="add-pricepointing-container pl-2">
                    <v-icon small class="add-pricepointing-icon" @click="startPricePointingCreation"
                      >fa-plus-square</v-icon
                    >
                    <span class="add-pricepointing-text">{{
                      $t('settings.penaltyRules.pricePointing.addRangeBreak')
                    }}</span>
                  </div>
                </v-container>

                <v-layout
                  v-if="
                    item.penalty !== penaltyCategories.pricePointing &&
                      item.penalty !== penaltyCategories.maxUpDown
                  "
                >
                  <v-flex v-if="isFixedCompetitorPenaltyWeightEnabled(item)" xs3>
                    <v-form
                      ref="fixedPenaltyForm"
                      v-model="isFixedPenaltyFormValid"
                      class="fixed-competitor__form mr-5 pa-4 pt-2"
                      :class="{ opaque: !mutableRule.competitorPenalty.weighted }"
                    >
                      <v-row>
                        <v-col
                          class="font-weight-bold"
                          :class="{ 'disabled-text': !mutableRule.competitorPenalty.weighted }"
                        >
                          {{ $t('settings.penaltyRules.totalWeightCompetitorEngine') }}
                        </v-col>
                      </v-row>

                      <table
                        class="fixed-competitor__penalty-table fixed-competitor__penalty-table--narrow"
                      >
                        <thead>
                          <tr>
                            <th>{{ $t('settings.penaltyRules.sideOfRefPrice') }}</th>
                            <th>{{ $t('settings.penaltyRules.maxPenalty') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{{ $t('settings.penaltyRules.lower') }}</td>
                            <td>
                              <v-text-field
                                v-model.number="maxPenaltyBelow"
                                class="mt-0 pt-0"
                                flat
                                reverse
                                hide-details
                                type="number"
                                min="0"
                                :rules="[rules.required, rules.valueIsPositiveOrZero]"
                                :background-color="owWhite"
                                :disabled="!mutableRule.competitorPenalty.weighted"
                                @input="updateA2D2points"
                              />
                            </td>
                          </tr>
                          <tr>
                            <td>{{ $t('settings.penaltyRules.upper') }}</td>
                            <td>
                              <v-text-field
                                v-model.number="maxPenaltyAbove"
                                class="mt-0 pt-0"
                                flat
                                reverse
                                hide-details
                                type="number"
                                min="0"
                                :rules="[rules.required, rules.valueIsPositiveOrZero]"
                                :background-color="owWhite"
                                :disabled="!mutableRule.competitorPenalty.weighted"
                                @input="updateA2D2points"
                              />
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      <v-row class="mt-3">
                        <v-col
                          class="font-weight-bold"
                          :class="{ 'disabled-text': !mutableRule.competitorPenalty.weighted }"
                        >
                          {{ $t('settings.penaltyRules.distributionOfWeights') }}
                        </v-col>
                      </v-row>
                      <div class="fixed-competitor__penalty-table-container">
                        <table class="fixed-competitor__penalty-table">
                          <thead>
                            <tr>
                              <th>{{ $t('settings.penaltyRules.competitor') }}</th>
                              <th>{{ $t('settings.penaltyRules.weightBelow') }}</th>
                              <th>{{ $t('settings.penaltyRules.weightAbove') }}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="competitor in activeCompetitorsPenalties"
                              :key="competitor.competitorKey"
                            >
                              <td>{{ competitor.competitorName }}</td>
                              <td class="input-column">
                                <v-text-field
                                  class="mt-0 pt-0"
                                  flat
                                  reverse
                                  hide-details
                                  type="number"
                                  min="0"
                                  :value="
                                    get(
                                      mutableRule,
                                      `competitorPenalty.weights.below[${
                                        competitor.competitorName
                                      }]`
                                    )
                                  "
                                  :rules="[rules.required, rules.valueIsPositiveOrZero]"
                                  :background-color="owWhite"
                                  :disabled="!mutableRule.competitorPenalty.weighted"
                                  @input="
                                    value => onWeightInput({ value, competitor, type: 'below' })
                                  "
                                />
                              </td>
                              <td class="input-column">
                                <v-text-field
                                  class="mt-0 pt-0"
                                  flat
                                  reverse
                                  hide-details
                                  type="number"
                                  min="0"
                                  :value="
                                    get(
                                      mutableRule,
                                      `competitorPenalty.weights.above[${
                                        competitor.competitorName
                                      }]`
                                    )
                                  "
                                  :rules="[rules.required, rules.valueIsPositiveOrZero]"
                                  :background-color="owWhite"
                                  :disabled="!mutableRule.competitorPenalty.weighted"
                                  @input="
                                    value => onWeightInput({ value, competitor, type: 'above' })
                                  "
                                />
                              </td>
                            </tr>
                            <tr class="text-right font-weight-bold">
                              <td>
                                {{ $t('settings.penaltyRules.total') }}
                              </td>
                              <td class="input-column">
                                {{ getWeightsSum('below') }}
                              </td>
                              <td class="input-column">
                                {{ getWeightsSum('above') }}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </v-form>
                  </v-flex>

                  <v-flex
                    v-bind="{
                      xs9: isFixedCompetitorPenaltyWeightEnabled(item),
                      xs12: !isFixedCompetitorPenaltyWeightEnabled(item),
                    }"
                  >
                    <v-layout>
                      <!-- <form novalidate> is required to disable browser native validation  -->
                      <form
                        :class="{
                          formWrapper: !(
                            item.penalty === penaltyCategories.storeGroup &&
                            isStoreGroupMaxUpDownEnabled
                          ),
                          'formWrapper--weighted': isWeightedRule(item),
                          'panels-form':
                            item.penalty === penaltyCategories.storeGroup &&
                            isStoreGroupMaxUpDownEnabled,
                        }"
                        novalidate
                      >
                        <v-flex
                          xs8
                          :class="{
                            'panels-stretch':
                              item.penalty === penaltyCategories.storeGroup &&
                              isStoreGroupMaxUpDownEnabled,
                          }"
                        >
                          <div
                            class="d-flex"
                            :class="{
                              'panels-row':
                                item.penalty === penaltyCategories.storeGroup &&
                                isStoreGroupMaxUpDownEnabled,
                            }"
                          >
                            <!-- Max Up/Down settings (left panel, store group only) -->
                            <store-group-max-up-down-panel
                              v-if="
                                item.penalty === penaltyCategories.storeGroup &&
                                  isStoreGroupMaxUpDownEnabled
                              "
                              :selected-store-group-penalty="selectedStoreGroupPenalty"
                              @validationChange="onStoreGroupMaxUpDownValidationChange"
                            />

                            <!-- Regular Offsets (relative % and absolute $) -->
                            <div
                              :class="{
                                'store-group-offsets-panel':
                                  item.penalty === penaltyCategories.storeGroup &&
                                  isStoreGroupMaxUpDownEnabled,
                                'offset-panel-box':
                                  item.penalty === penaltyCategories.storeGroup &&
                                  isStoreGroupMaxUpDownEnabled,
                              }"
                            >
                              <v-layout
                                align-baseline
                                :class="{ opaque: isExactMatchDisablingOffsets(item) }"
                              >
                                <v-text-field
                                  flat
                                  :background-color="owWhite"
                                  :label="$t('settings.penaltyRules.offset')"
                                  type="number"
                                  :value="getPenaltyRuleFromItem(item).penaltyOffset"
                                  :rules="rules.offsetInput"
                                  :disabled="
                                    isTabDisabled(item) || isExactMatchDisablingOffsets(item)
                                  "
                                  @input="value => debounceOffsetChange(item, value)"
                                />
                                <span class="suffix"> % </span>
                              </v-layout>
                              <v-layout
                                v-if="
                                  [
                                    penaltyCategories.competitor,
                                    penaltyCategories.minMax,
                                    penaltyCategories.storeGroup,
                                  ].includes(item.penalty)
                                "
                                align-baseline
                              >
                                <span class="prefix">
                                  {{ $t('clientCurrencySymbol') }}
                                </span>
                                <v-text-field
                                  :value="
                                    formatNumber({
                                      number: getPenaltyRuleFromItem(item).absoluteOffset || 0,
                                      format: numberFormats.priceFormatNegative,
                                    })
                                  "
                                  type="text"
                                  :background-color="owWhite"
                                  :label="$t('settings.penaltyRules.absoluteOffset')"
                                  :disabled="
                                    isTabDisabled(item) || isExactMatchDisablingOffsets(item)
                                  "
                                  @keypress="ensureDecimal($event, true)"
                                  @focus="previousFieldValue = $event.target.value"
                                  @blur="verifyFieldValue"
                                  @change="absoluteOffsetChange($event, item)"
                                />
                              </v-layout>
                            </div>
                          </div>
                        </v-flex>
                      </form>
                      <v-flex
                        layout
                        pt-8
                        class="chart-container"
                        :class="{ opaque: shouldDisableChartAndParameters(item) }"
                        v-bind="{
                          xs5: isFixedCompetitorPenaltyWeightEnabled(item),
                          xs6: !isFixedCompetitorPenaltyWeightEnabled(item),
                        }"
                      >
                        <penalty-chart
                          :key="forceRefresh[item.penalty]"
                          :penalty-category="item.penalty"
                          :penalty-curve-points="getPenaltyRuleFromItem(item)"
                          :is-weighted="isWeightedRule(item)"
                        />
                      </v-flex>
                      <v-flex
                        layout
                        v-bind="{
                          xs5: isFixedCompetitorPenaltyWeightEnabled(item),
                          xs4: !isFixedCompetitorPenaltyWeightEnabled(item),
                        }"
                        :class="{ opaque: shouldDisableChartAndParameters(item) }"
                      >
                        <v-layout justify-space-between>
                          <div
                            class="formWrapper mr-3"
                            :class="{ 'formWrapper--weighted': isWeightedRule(item) }"
                          >
                            <penalty-parameters-form
                              penalty-name="lower"
                              class="penalty-form"
                              :penalty-rule="getPenaltyParameters(item, 'lower')"
                              :disabled="
                                isTabDisabled(item) || shouldDisableChartAndParameters(item)
                              "
                              :is-weighted="isWeightedRule(item)"
                              @updatePenaltyRule="event => updatePenaltyRuleHandler(item, event)"
                              @penaltyRuleInvalid="validRule = false"
                            />
                          </div>
                          <div
                            class="formWrapper"
                            :class="{ 'formWrapper--weighted': isWeightedRule(item) }"
                          >
                            <penalty-parameters-form
                              penalty-name="upper"
                              class="penalty-form"
                              :penalty-rule="getPenaltyParameters(item, 'upper')"
                              :is-weighted="isWeightedRule(item)"
                              :disabled="
                                isTabDisabled(item) || shouldDisableChartAndParameters(item)
                              "
                              @updatePenaltyRule="event => updatePenaltyRuleHandler(item, event)"
                              @penaltyRuleInvalid="validRule = false"
                            />
                          </div>
                        </v-layout>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                </v-layout>
              </v-tab-item>
            </v-tabs-items>

            <v-layout align-baseline justify-space-between>
              <div class="weighted-penalty-warning">
                <span v-if="tab === 1 && mutableRule.competitorPenalty.weighted">
                  {{ $t('settings.penaltyRules.fixedCompetitorWarning') }}
                </span>
              </div>
              <div class="mr-6">
                <v-btn
                  v-show="!disabled"
                  class="mt-2 mr-2"
                  small
                  color="primary"
                  outlined
                  depressed
                  @click="cancel"
                >
                  {{ $t('actions.cancel') }}
                </v-btn>
                <div v-if="isSomeWeightedCompetitorsInvalid" class="error-text mr-2">
                  {{ $t('settings.penaltyRules.allWeightedCompetitorsInvalid') }}
                </div>
                <div v-if="moreThanOneActiveCompetitorExactMatch" class="error-text mr-2">
                  {{ $t('settings.penaltyRules.exactMatchInvalid') }}
                </div>
                <v-btn
                  v-show="!disabled"
                  color="success"
                  :disabled="disableSaveBtn"
                  class="mt-2 save"
                  small
                  depressed
                  @click="save"
                >
                  {{ $t('actions.save') }}
                </v-btn>
              </div>
            </v-layout>
          </v-card>
        </v-card>
      </v-flex>
    </v-expand-transition>

    <!-- Fixed competitor penalty weight confirm dialog -->
    <confirm-dialog
      :is-open="isFixedCompetitorPenaltyWeightDialogOpen"
      :ok-btn-text="$t('actions.yesProceed')"
      @onOk="enableFixedCompetitorPenaltyWeight"
      @onCancel="closeFixedCompetitorPenaltyWeightDialogOpen"
    >
      <template v-slot:body>
        {{ $t('dialogs.fixedCompetitorPenaltyWeight.header') }}
      </template>
    </confirm-dialog>

    <!-- Exact match confirm dialog (competitor only) -->
    <confirm-dialog
      :is-open="isCompetitorExactMatchDialogOpen"
      :ok-btn-text="$t('actions.yesProceed')"
      @onOk="enableCompetitorExactMatch"
      @onCancel="closeCompetitorExactMatchDialog"
    >
      <template v-slot:body>
        {{ $t('dialogs.exactCompetitorMatch.header') }}
      </template>
    </confirm-dialog>
  </div>
</template>

<script>
import {
  get,
  cloneDeep,
  isEqual,
  isEmpty,
  debounce,
  map,
  sortBy,
  find,
  round,
  forEach,
  uniq,
  uniqBy,
  size,
  some,
  isNil,
} from 'lodash';
import { mapState, mapGetters, mapActions } from 'vuex';
import { active, inactive } from '@enums/rule-status';
import { edit, create } from '@enums/dialog-modes';
import penaltyCategories from '@enums/penalty-categories';
import storeGroupPenaltyTab from './store-group-penalty-tab.vue';
import numberFormats from '@enums/number-formats';
import {
  displayStoreGroupPenalty,
  displayMinMaxPenalty,
  displayFixedCompetitorPenaltyWeight,
  fixedCompetitorPenaltyWeightOptions,
  displayMaxUpDown,
} from '@enums/feature-flags';
import { storegroupOption } from '@enums/filter-options';
import featureFlagsMixin from '../../../../mixins/featureFlags';
import colours from '../../../../ow-colors';
import storeGroupMaxUpDownPanel from './store-group-max-up-down-panel.vue';
import { priceGroupOverrideRule, pricingGroupRule } from '@enums/rule-type';
import { architectureGroupLevel, pricingGroupLevel } from '@enums/hierarchy';
import clientConfig from '@sharedModules/config/client';

export default {
  components: {
    storeGroupPenaltyTab,
    storeGroupMaxUpDownPanel,
  },
  mixins: [featureFlagsMixin],
  props: {
    rulesetType: {
      type: String,
      required: true,
    },
    ruleIndex: {
      type: Number,
      required: true,
    },
    rule: {
      type: Object,
      required: true,
    },
    mode: {
      type: String,
      default: edit,
    },
    productCounts: {
      type: Array,
      default: () => [],
    },
    expandedKey: {
      type: String,
      default: () => '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
    /** The penalty category tab to auto-select when this rule is opened via the product reveal. */
    initialPenaltyCategory: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      forceRefresh: {},
      owWhite: colours.owWhite,
      tab: null,
      // TODO: look into vuelidate to check form input is dirty, (i.e. form has changed since it was last submitted)
      // rather than keeping multiple clones of the same object.
      // https://github.com/vuelidate/vuelidate
      mutableRule: cloneDeep(this.rule),
      savedRule: cloneDeep(this.rule), // overwritten with updates after Save pressed
      tabs: [],
      active,
      inactive,
      // these are not really indexes, but keys
      selectedCompetitorIndex: 0,
      selectedStoreGroupIndex: 0,
      selectedMinMaxStoreGroupIndex: 0, // assume 0 is never a real key. Also real tsgkeys are strings. will get set in created hook
      offset: 0, // hack: This is left in so we start with a valid offset. It's updated to the right value before any checks.
      validRule: true,
      previousFieldValue: 0,
      requiredRule: v => !!v || v === 0 || this.$t('settings.parameters.errors.fieldRequired'),
      competitorDropdownValues: [],
      storeGroupDropdownValues: [],
      storeGroupPenaltyErrors: {},
      penaltyCategories,
      numberFormats,
      isFixedCompetitorPenaltyWeightDialogOpen: false,
      isFixedPenaltyFormValid: true,
      isCompetitorExactMatchDialogOpen: false,
      get,
    };
  },

  computed: {
    ...mapGetters('competitorMetadata', ['competitors']),
    ...mapState('workpackages', ['selectedWorkpackageToolStoreGroups']),
    ...mapState('clientConfig', ['toggleLogic', 'releaseFlags']),

    rules() {
      return {
        required: this.requiredRule,
        valueIsPositiveOrZero: v =>
          v >= 0 || this.$t('settings.parameters.errors.positiveOrZeroValidation'),
        offsetInput: [
          v => !!v || v === 0 || this.$t('settings.parameters.errors.fieldRequired'),
          v =>
            (v >= -100 && v <= 100) ||
            this.$t('settings.parameters.errors.minMaxValidation', { min: -100, max: 100 }),
        ],
      };
    },

    enableArchitectureFilter() {
      return [priceGroupOverrideRule, pricingGroupRule].includes(this.rulesetType);
    },

    displayCompetitorExactMatch() {
      return this.releaseFlags.enableCompetitorExactMatch;
    },

    displayStoreGroupExactMatch() {
      return this.releaseFlags.enableStoreGroupExactMatch;
    },

    displayStoreGroupMaxUpDown() {
      return this.releaseFlags.enableStoreGroupMaxUpDown;
    },

    allStoreGroupDropdownValues() {
      return this.allToolStoreGroupKeys.map(key => ({
        text: this.toolStoreGroupMap[key],
        value: key,
      }));
    },

    hierarchyFilterLevel() {
      if (this.enableArchitectureFilter) {
        return architectureGroupLevel;
      }
      return pricingGroupLevel;
    },

    toolStoreGroupMap() {
      return this.selectedWorkpackageToolStoreGroups.reduce((result, tsg) => {
        result[tsg.key] = tsg.description;
        result[tsg.description] = tsg.key;
        return result;
      }, {});
    },

    allToolStoreGroupKeys() {
      return isEmpty(this.selectedWorkpackageToolStoreGroups)
        ? null
        : this.selectedWorkpackageToolStoreGroups.map(tsg => tsg.key);
    },

    selectedCompetitorPenalty() {
      return find(this.mutableRule.competitorPenalty.penalties, {
        competitorKey: this.selectedCompetitorIndex,
      });
    },

    selectedStoreGroupPenalty() {
      return find(this.mutableRule.storeGroupPenalty.penalties, {
        toolStoreGroupKey: this.selectedStoreGroupIndex,
      });
    },

    selectedMinMaxPenalty() {
      return find(this.mutableRule.minMaxPenalty.penalties, {
        toolStoreGroupKey: this.selectedMinMaxStoreGroupIndex,
      });
    },

    competitorIsActive: {
      get() {
        return isEmpty(this.selectedCompetitorPenalty)
          ? false
          : this.selectedCompetitorPenalty.isActive;
      },
      set(value) {
        this.$set(this.selectedCompetitorPenalty, 'isActive', value);
      },
    },

    competitorIsExactMatch: {
      get() {
        if (isEmpty(this.selectedCompetitorPenalty)) return false;
        return this.selectedCompetitorPenalty.exactMatch;
      },
      set(value) {
        this.$set(this.selectedCompetitorPenalty, 'exactMatch', value);
      },
    },

    minMaxStoreGroupIsActive: {
      get() {
        if (isEmpty(this.selectedMinMaxPenalty)) return false;
        return this.selectedMinMaxPenalty.isActive;
      },
      set(value) {
        this.$set(this.selectedMinMaxPenalty, 'isActive', value);
      },
    },

    // Store Group state (read-only, for chart/parameters control)
    storeGroupIsExactMatch() {
      if (isEmpty(this.selectedStoreGroupPenalty)) return false;
      return get(this.selectedStoreGroupPenalty, 'exactMatch', false);
    },

    isStoreGroupMaxUpDownEnabled() {
      if (!this.displayStoreGroupMaxUpDown) return false;
      if (isEmpty(this.selectedStoreGroupPenalty)) return false;
      return get(this.selectedStoreGroupPenalty, 'maxUpDown.isActive', false);
    },

    isEditMode() {
      return this.mode === edit;
    },

    isCreateMode() {
      return this.mode === create;
    },

    duplicateRangeMaximumsInPricePointing() {
      return (
        size(this.mutableRule.pricePointing.allowedValues) !==
        size(uniqBy(this.mutableRule.pricePointing.allowedValues, 'rangeMaximum'))
      );
    },

    missingEmptyPricePointingRange() {
      return !some(this.mutableRule.pricePointing.allowedValues, { rangeMaximum: null });
    },

    missingTokensForPricePointingRange() {
      return this.mutableRule.pricePointing.allowedValues.some(pp => isEmpty(pp.offsets));
    },

    moreThanOneActiveCompetitorExactMatch() {
      return (
        this.mutableRule.competitorPenalty.penalties.filter(
          competitor => competitor.exactMatch && competitor.isActive
        ).length > 1
      );
    },

    disableSaveBtn() {
      const isWeightedValid = this.mutableRule.competitorPenalty.weighted
        ? this.isFixedPenaltyFormValid
        : true;

      // Check if there are store group validation errors
      const hasStoreGroupErrors = Object.keys(this.storeGroupPenaltyErrors).length > 0;

      if (
        !this.disabled &&
        isWeightedValid &&
        !this.isSomeWeightedCompetitorsInvalid &&
        this.validRule &&
        (this.isEditMode || this.isCreateMode) &&
        this.validateMinMax(this.offset, -100, 100) &&
        !this.duplicateRangeMaximumsInPricePointing &&
        !this.missingEmptyPricePointingRange &&
        !this.missingTokensForPricePointingRange &&
        !this.moreThanOneActiveCompetitorExactMatch &&
        !hasStoreGroupErrors
      ) {
        return isEqual(this.mutableRule, this.savedRule);
      }
      return true;
    },

    visibleTabs() {
      const tabs = [
        { id: penaltyCategories.economic },
        { id: penaltyCategories.competitor },
        { id: penaltyCategories.architecture },
        { id: penaltyCategories.minMax, featureFlag: displayMinMaxPenalty },
        { id: penaltyCategories.storeGroup, featureFlag: displayStoreGroupPenalty },
        { id: penaltyCategories.maxUpDown, featureFlag: displayMaxUpDown },
        { id: penaltyCategories.pricePointing },
      ];
      return this.filterArrayByFeatureFlag(tabs).map(tab => tab.id);
    },

    storeGroupTab() {
      return this.tabs.find(tab => tab.penalty === penaltyCategories.storeGroup);
    },

    selectedToolStoreGroupsInScope() {
      const res = this.storeGroupTab ? this.getToolStoreGroupsFromScope(this.mutableRule) : [];

      return res;
    },

    activeCompetitorsPenalties() {
      return this.mutableRule.competitorPenalty.penalties.filter(competitor => competitor.isActive);
    },

    maxPenaltyBelow: {
      get() {
        return get(this.mutableRule, 'competitorPenalty.maxPenalty.below');
      },
      set(value) {
        this.$set(this.mutableRule.competitorPenalty.maxPenalty, 'below', value);
      },
    },

    maxPenaltyAbove: {
      get() {
        return get(this.mutableRule, 'competitorPenalty.maxPenalty.above');
      },
      set(value) {
        this.$set(this.mutableRule.competitorPenalty.maxPenalty, 'above', value);
      },
    },

    isSomeWeightedCompetitorsInvalid() {
      return (
        this.mutableRule.competitorPenalty.weighted &&
        this.activeCompetitorsPenalties.some(penalty => {
          const b2percentage = penalty.lower.penaltyDeviation[1].penaltyAbsolute;
          const c2percentage = penalty.upper.penaltyDeviation[0].penaltyAbsolute;
          return (
            isNil(b2percentage) ||
            isNil(c2percentage) ||
            b2percentage < 0 ||
            b2percentage > 100 ||
            c2percentage < 0 ||
            c2percentage > 100
          );
        })
      );
    },

    // Max Up/Down block computed helpers
    maxUpDownBlock: {
      get() {
        return this.mutableRule.maxUpDown || {};
      },
      set(value) {
        this.$set(this.mutableRule, 'maxUpDown', value);
      },
    },
    maxUpDownIsActive: {
      get() {
        return get(this.maxUpDownBlock, 'isActive', false);
      },
      set(v) {
        this.maxUpDownBlock = { ...this.maxUpDownBlock, isActive: v };
      },
    },
    applyMaxUp: {
      get() {
        return get(this.maxUpDownBlock, 'applyMaxUp', false);
      },
      set(v) {
        this.maxUpDownBlock = { ...this.maxUpDownBlock, applyMaxUp: v };
      },
    },
    applyMaxDown: {
      get() {
        return get(this.maxUpDownBlock, 'applyMaxDown', false);
      },
      set(v) {
        this.maxUpDownBlock = { ...this.maxUpDownBlock, applyMaxDown: v };
      },
    },
    maxUpPctUi: {
      get() {
        const val = get(this.maxUpDownBlock, 'maxUpPct', 0);
        return Number(((val || 0) * 100).toFixed(1));
      },
      set(v) {
        const num = Number(v);
        const pctRounded = Number.isNaN(num) ? 0 : Number(num.toFixed(1));
        const nonNegativePct = Math.max(pctRounded, 0);
        const decimal = Number((nonNegativePct / 100).toFixed(3));
        this.maxUpDownBlock = { ...this.maxUpDownBlock, maxUpPct: decimal };
      },
    },
    maxDownPctUi: {
      get() {
        const val = get(this.maxUpDownBlock, 'maxDownPct', 0);
        return Number(((val || 0) * 100).toFixed(1));
      },
      set(v) {
        const num = Number(v);
        const pctRounded = Number.isNaN(num) ? 0 : Number(num.toFixed(1));
        const nonNegativePct = Math.max(pctRounded, 0);
        const decimal = Number((nonNegativePct / 100).toFixed(3));
        this.maxUpDownBlock = { ...this.maxUpDownBlock, maxDownPct: decimal };
      },
    },
  },

  watch: {
    // TODO: think a better way of handling this. using watcher seems expensive
    // Update mutableRule and tabs whenever this.rule changes
    rule() {
      this.resetMutableRule();
    },
  },

  async created() {
    this.tabs = this.generateTabs();
    this.competitorDropdownValues = this.getCompetitorDropdownValues();
    this.selectedCompetitorIndex = this.competitorDropdownValues[0].value;
    this.selectedMinMaxStoreGroupIndex = this.allStoreGroupDropdownValues[0].value;
    this.updateStoreGroupTabData();
    this.setInitialTab();
  },

  methods: {
    ...mapActions('rules', ['deleteRule', 'copyRule']),

    /**
     * Handles the copyRule event emitted by the rule-header component.
     * @param {{ rule: Object }} payload - The rule to copy.
     */
    handleCopyRule({ rule }) {
      this.copyRule({ rule });
    },

    isTabDisabled(tab) {
      return this.disabled || !tab || tab.disabled;
    },

    generateTabs() {
      return this.visibleTabs.map(penalty => {
        const penaltyKey = this.getPenaltyPropertyName(penalty);
        const localizationSuffix = this.getLocalizationSuffix(penalty);
        return {
          penalty: `${penalty}`,
          iconText: this.$t(`settings.penaltyRules.${localizationSuffix}Initial`),
          tabText: this.$t(`settings.penaltyRules.${localizationSuffix}TabText`),
          status: get(this.mutableRule, [`${penaltyKey}`, 'status'], this.inactive),
          iconClass: `${penalty}-penalty-traffic-light`,
          rule: this.mutableRule[penaltyKey],
        };
      });
    },

    getCompetitorDropdownValues() {
      return map(this.competitors, item => ({
        text: item.competitorDisplayDescription,
        value: item.competitorKey,
      }));
    },

    getPenaltyPropertyName(penalty) {
      // Penalties that don't use the "Penalty" suffix
      const nonPenaltySuffixPenalties = [
        penaltyCategories.pricePointing,
        penaltyCategories.maxUpDown,
      ];
      return nonPenaltySuffixPenalties.includes(penalty) ? penalty : `${penalty}Penalty`;
    },

    getLocalizationSuffix(penalty) {
      // Penalties that don't use the "Penalty" suffix in localization keys
      const nonPenaltySuffixPenalties = [
        penaltyCategories.pricePointing,
        penaltyCategories.maxUpDown,
      ];
      return nonPenaltySuffixPenalties.includes(penalty) ? penalty : `${penalty}Penalty`;
    },

    setStatus(item) {
      const penaltyKey = this.getPenaltyPropertyName(item.penalty);
      if (!this.mutableRule[penaltyKey]) {
        this.$set(this.mutableRule, penaltyKey, {});
      }
      this.$set(this.mutableRule[penaltyKey], 'status', item.status);
    },

    updateRuleScope(newScope) {
      this.mutableRule.scope = cloneDeep(newScope);
      this.updateStoreGroupTabData();
    },

    getStoreGroupDropdownValues(toolStoreGroupFilters) {
      const keys = this.allToolStoreGroupKeys.filter(key => !toolStoreGroupFilters.includes(key));
      return keys.map(key => ({
        text: this.toolStoreGroupMap[key],
        value: key,
      }));
    },

    deactivateStoreGroupPenalties() {
      const nonSelectedPenalties = this.mutableRule.storeGroupPenalty.penalties.filter(
        penalty =>
          penalty.toolStoreGroupKey && penalty.toolStoreGroupKey !== this.selectedStoreGroupIndex
      );
      if (isEmpty(nonSelectedPenalties)) return;
      nonSelectedPenalties.forEach(penalty => {
        if (penalty.isActive) {
          penalty.isActive = false;
        }
      });
    },

    setPreselectedStoreGroup() {
      if (!this.storeGroupDropdownValues.length) {
        this.selectedStoreGroupIndex = null;
        return;
      }
      const selectedStoreGroup = this.storeGroupDropdownValues.find(
        item => item.value === this.selectedStoreGroupIndex
      );
      if (selectedStoreGroup) {
        return;
      }
      this.selectedStoreGroupIndex = this.storeGroupDropdownValues[0].value;
    },

    resetStoreGroupTabToInitialState() {
      this.storeGroupTab.disabled = true;
      this.storeGroupTab.tooltipMessage = this.$t('settings.penaltyRules.disabledStoreGroupTab');
      this.selectedStoreGroupIndex = null;
      this.storeGroupDropdownValues = [];
      const newPenalties = this.mutableRule.storeGroupPenalty.penalties.filter(
        penalty => !penalty.toolStoreGroupKey
      );
      this.$set(this.mutableRule.storeGroupPenalty, 'penalties', newPenalties);
      if (this.storeGroupTab.status !== inactive) {
        this.$set(this.storeGroupTab, 'status', inactive);
        this.setStatus(this.storeGroupTab);
      }
    },

    removeUnusedStoreGroupPenalties() {
      if (!this.storeGroupDropdownValues || !this.storeGroupDropdownValues.length) {
        const newPenalties = this.mutableRule.storeGroupPenalty.penalties.filter(
          penalty => !penalty.toolStoreGroupKey
        );
        this.$set(this.mutableRule.storeGroupPenalty, 'penalties', newPenalties);
        return;
      }
      const newPenalties = this.mutableRule.storeGroupPenalty.penalties.filter(
        penalty =>
          !penalty.toolStoreGroupKey ||
          this.storeGroupDropdownValues.find(group => group.value === penalty.toolStoreGroupKey)
      );
      this.$set(this.mutableRule.storeGroupPenalty, 'penalties', newPenalties);
    },

    updateStoreGroupTabData() {
      if (!this.storeGroupTab) {
        return;
      }
      if (isEmpty(this.selectedToolStoreGroupsInScope)) {
        this.resetStoreGroupTabToInitialState();
        return;
      }
      this.storeGroupTab.tooltipMessage = null;
      this.storeGroupDropdownValues = this.getStoreGroupDropdownValues(
        this.selectedToolStoreGroupsInScope
      );
      this.setPreselectedStoreGroup();
      this.removeUnusedStoreGroupPenalties();
      this.storeGroupTab.disabled = !this.selectedStoreGroupIndex;
      if (this.storeGroupTab.status !== inactive && this.storeGroupTab.disabled) {
        this.$set(this.storeGroupTab, 'status', inactive);
        this.setStatus(this.storeGroupTab);
      }
    },

    /**
     * Receives the current validation state from store-group-max-up-down-panel.
     * @param {{ isValid: boolean, errors: Object }} payload
     */
    onStoreGroupMaxUpDownValidationChange({ isValid, errors }) {
      this.storeGroupPenaltyErrors = isValid ? {} : errors;
    },

    getCompetitorRuleFromItem(item) {
      let penalty = find(item.rule.penalties, { competitorKey: this.selectedCompetitorIndex });

      if (!item.rule.penalties || !penalty) {
        penalty = {
          ...item.rule.defaultPenalty,
          competitorKey: this.selectedCompetitorIndex,
          competitorName: find(this.competitors, { competitorKey: this.selectedCompetitorIndex })
            .competitorDescription,
          isActive: false,
        };

        if (item.rule.weighted) {
          this.setWeightedPenaltyDefaults(penalty);
        }

        item.rule.penalties.push(penalty);
      }
      return penalty;
    },

    getMinMaxRuleFromItem(item) {
      let penalty = find(item.rule.penalties, {
        toolStoreGroupKey: this.selectedMinMaxStoreGroupIndex,
      });
      if (!item.rule.penalties || !penalty) {
        penalty = {
          ...item.rule.defaultPenalty,
          toolStoreGroupKey: this.selectedMinMaxStoreGroupIndex,
          isActive: false,
        };
        item.rule.penalties.push(penalty);
      }
      return penalty;
    },

    getStoreGroupRuleFromItem(item) {
      let penalty = find(item.rule.penalties, { toolStoreGroupKey: this.selectedStoreGroupIndex });
      if (!item.rule.penalties || !penalty) {
        penalty = {
          ...item.rule.defaultPenalty,
          toolStoreGroupKey: this.selectedStoreGroupIndex,
          isActive: false,
          maxUpDown: {
            isActive: false,
            offsetType: 'pct',
            applyMaxUp: false,
            maxUp: null,
            applyMaxDown: false,
            maxDown: null,
          },
        };
        item.rule.penalties.push(penalty);
      }
      return penalty;
    },

    getPenaltyRuleFromItem(item) {
      if (item.penalty === penaltyCategories.competitor) {
        return this.getCompetitorRuleFromItem(item);
      }
      if (item.penalty === penaltyCategories.storeGroup) {
        return this.getStoreGroupRuleFromItem(item);
      }
      if (item.penalty === penaltyCategories.minMax) {
        return this.getMinMaxRuleFromItem(item);
      }
      return item.rule;
    },

    getPenaltyParameters(item, penaltyName) {
      const penaltyRule = this.getPenaltyRuleFromItem(item);
      return get(penaltyRule, penaltyName);
    },

    resetMutableRule() {
      this.mutableRule = cloneDeep(this.rule);
      this.tabs = this.generateTabs();
      this.updateStoreGroupTabData();
    },

    cancel() {
      if (this.isEditMode) {
        this.resetMutableRule();
        this.toggleExpanded(this.rulesetType, this.ruleIndex);
        return;
      }
      this.$emit('cancelNewRule');
    },

    removeUnusedDataFromRuleBeforeSaving(rule) {
      if (rule.storeGroupPenalty) {
        const group = rule.storeGroupPenalty;
        group.penalties = group.penalties.filter(penalty => !!penalty.toolStoreGroupKey);
      }
    },

    async save() {
      if (this.mutableRule.competitorPenalty.weighted) {
        this.updateA2D2points();
        // do not save inactive competitors
        this.mutableRule.competitorPenalty.penalties = this.activeCompetitorsPenalties;
      }
      // sort the price pointing entries on rangeMaximum, null values for rangeMaximum should be at bottom
      const sortedPricePointingAllowedValues = sortBy(
        this.mutableRule.pricePointing.allowedValues,
        'rangeMaximum'
      );
      forEach(sortedPricePointingAllowedValues, allowedValue => {
        allowedValue.offsets = sortBy(uniq(allowedValue.offsets));
      });
      this.$set(this.mutableRule.pricePointing, 'allowedValues', sortedPricePointingAllowedValues);
      const rule = cloneDeep(this.mutableRule);
      this.removeUnusedDataFromRuleBeforeSaving(rule);
      if (!this.isEditMode) {
        this.$emit('saveNewRule', rule, this.rulesetType);
        return;
      }
      // updates object can't contain _id field
      const { _id, ...updates } = rule;
      let mostRecentTab;
      if (!isEmpty(this.forceRefresh)) {
        mostRecentTab = Object.keys(this.forceRefresh).reduce((key, v) =>
          this.forceRefresh[v] > this.forceRefresh[key] ? v : key
        );
      } else mostRecentTab = penaltyCategories.economic;
      await this.$emit('updateRule', { id: this.savedRule._id, updates });
      this.savedRule = cloneDeep(this.mutableRule);
      // If price pointing has been updated we do not collapse the rule
      // and it is the most recent tab opened
      if (mostRecentTab !== penaltyCategories.pricePointing)
        this.toggleExpanded(this.rulesetType, this.ruleIndex);
    },

    updatePenaltyRuleHandler(item, { penaltyName, updatedParameters }) {
      this.validRule = true; // this handler is only triggered if the other params are already valid.
      if (this.validateMinMax(this.offset, -100, 100)) {
        // all other validations handled in penalty-parameters-form
        const penaltyRule = this.getPenaltyRuleFromItem(item);
        penaltyRule[penaltyName] = cloneDeep(updatedParameters);
      }
    },

    verifyFieldValue(event) {
      const value = event.target.value;
      if (Number(value.replace(this.$t('number.decimalPoint'), '.')) === 0) return;
      if (!value.trim() || !Number(value.replace(this.$t('number.decimalPoint'), '.'))) {
        // restores previous value in case of input field value is not valid
        event.target.value = this.previousFieldValue;
      }
    },

    absoluteOffsetChange(newValue, item) {
      let absoluteOffset = Number(newValue.replace(this.$t('number.decimalPoint'), '.'));
      if (!absoluteOffset && absoluteOffset !== 0) return;
      absoluteOffset = round(absoluteOffset, 2);
      const rule = this.getPenaltyRuleFromItem(item);
      this.$set(rule, 'absoluteOffset', absoluteOffset);
    },

    debounceOffsetChange: debounce(function(item, value) {
      this.offset = value;
      const rule = this.getPenaltyRuleFromItem(item);
      rule.penaltyOffset = +value;
    }, clientConfig.inputDebounceValue),

    tabChange(item) {
      // updating key forces a refresh of the component
      // we need to force a refresh as when the chart loads
      // the parent element is 'display:none' there is no access to width
      // and height which highcharts requires to build out
      // the chart nicely.
      this.forceRefresh[item.penalty] = Date.now();
    },

    isCompetitorActive(index) {
      const competitor = find(this.mutableRule.competitorPenalty.penalties, {
        competitorKey: index,
      });
      return isEmpty(competitor) ? false : competitor.isActive;
    },

    isStoreGroupActive(index) {
      const storeGroup = find(this.mutableRule.storeGroupPenalty.penalties, {
        toolStoreGroupKey: index,
      });
      return isEmpty(storeGroup) ? false : storeGroup.isActive;
    },

    isMinMaxStoreGroupActive(index) {
      const storeGroup = find(this.mutableRule.minMaxPenalty.penalties, {
        toolStoreGroupKey: index,
      });
      return isEmpty(storeGroup) ? false : storeGroup.isActive;
    },

    validateMinMax(val, min, max) {
      return val >= min && val <= max;
    },

    isExpanded(rulesetType, ruleIndex) {
      return this.expandedKey === `${rulesetType}-${ruleIndex}`;
    },

    /** Selects the penalty type tab matching initialPenaltyCategory when the rule is expanded from the product reveal. */
    setInitialTab() {
      if (!this.initialPenaltyCategory || !this.isExpanded(this.rulesetType, this.ruleIndex)) {
        return;
      }
      const tabIndex = this.tabs.findIndex(t => t.penalty === this.initialPenaltyCategory);
      if (tabIndex >= 0) {
        this.tab = tabIndex;
      }
    },

    toggleExpanded(rulesetType, ruleIndex) {
      if (this.isExpanded(rulesetType, ruleIndex)) {
        this.$emit('setExpandedKey', '');
        return;
      }
      this.$emit('setExpandedKey', `${rulesetType}-${ruleIndex}`);
    },

    updateDescription(description) {
      this.mutableRule.description = description;
    },

    startPricePointingCreation() {
      this.mutableRule.pricePointing.allowedValues.push({
        rangeMaximum: null,
        base: null,
        offsets: [],
      });
    },
    deletePricePointing({ index }) {
      this.mutableRule.pricePointing.allowedValues.splice(index, 1);
    },
    updateRangeMaximum({ updatedRangeMaximum, index }) {
      this.$set(
        this.mutableRule.pricePointing.allowedValues[index],
        'rangeMaximum',
        isEmpty(updatedRangeMaximum) ? null : Number(updatedRangeMaximum)
      );
    },
    updateOffset({ updatedOffsets, index }) {
      // ensures reactivity
      this.mutableRule.pricePointing.allowedValues[index].offsets = [];
      this.mutableRule.pricePointing.allowedValues[index].offsets.push(...updatedOffsets);
    },
    updateBase({ updatedBase, index }) {
      this.$set(this.mutableRule.pricePointing.allowedValues[index], 'base', Number(updatedBase));
    },
    /**
     * Returns keys of tool store groups selected in the scope of a rule.
     * @param rule
     * @return {string[]}
     */
    getToolStoreGroupsFromScope(rule) {
      if (!rule || !rule.scope || isEmpty(this.toolStoreGroupMap)) return [];
      const filter = rule.scope.find(attr => attr.attributeFilterType === storegroupOption);

      if (!filter || isEmpty(filter.attributeValue)) {
        return [];
      }

      return filter.attributeValue;
    },

    isFixedCompetitorPenaltyWeightEnabled(tab) {
      return (
        tab &&
        tab.penalty === penaltyCategories.competitor &&
        this.toggleLogic[displayFixedCompetitorPenaltyWeight]
      );
    },

    toggleFixedCompetitorPenaltyWeight() {
      if (this.mutableRule.competitorPenalty.weighted) {
        this.isFixedCompetitorPenaltyWeightDialogOpen = true;
        // switch off toggle, unless user confirms in modal
        this.$nextTick(() => {
          this.mutableRule.competitorPenalty.weighted = false;
        });
      } else {
        const defaultPenalty = this.mutableRule.competitorPenalty.defaultPenalty;
        this.mutableRule.competitorPenalty.penalties.forEach(penalty => {
          const newLower = cloneDeep(penalty.lower);
          if (!newLower.penaltyDeviation[0].penaltyAbsolute) {
            // A2 undefined - reset to defaults
            newLower.penaltyDeviation[0].penaltyAbsolute =
              defaultPenalty.lower.penaltyDeviation[0].penaltyAbsolute;
          }
          if (!newLower.penaltyDeviation[1].penaltyAbsolute) {
            // B2 undefined - reset to defaults
            newLower.penaltyDeviation[1].penaltyAbsolute =
              defaultPenalty.lower.penaltyDeviation[1].penaltyAbsolute;
          } else {
            // B2 should be converted back from percentage to absolute value
            newLower.penaltyDeviation[1].penaltyAbsolute = Math.round(
              (newLower.penaltyDeviation[0].penaltyAbsolute *
                newLower.penaltyDeviation[1].penaltyAbsolute) /
                100
            );
          }

          const newUpper = cloneDeep(penalty.upper);
          if (!newUpper.penaltyDeviation[1].penaltyAbsolute) {
            // D2 undefined - reset to defaults
            newUpper.penaltyDeviation[1].penaltyAbsolute =
              defaultPenalty.upper.penaltyDeviation[1].penaltyAbsolute;
          }

          if (!newUpper.penaltyDeviation[0].penaltyAbsolute) {
            // C2 undefined - reset to defaults
            newUpper.penaltyDeviation[0].penaltyAbsolute =
              defaultPenalty.upper.penaltyDeviation[0].penaltyAbsolute;
          } else {
            // C2 should be converted back from percentage to absolute value
            newUpper.penaltyDeviation[0].penaltyAbsolute = Math.round(
              (newUpper.penaltyDeviation[1].penaltyAbsolute *
                newUpper.penaltyDeviation[0].penaltyAbsolute) /
                100
            );
          }

          this.$set(penalty, 'lower', newLower);
          this.$set(penalty, 'upper', newUpper);
        });
      }
    },

    closeFixedCompetitorPenaltyWeightDialogOpen() {
      this.mutableRule.competitorPenalty.weighted = false;
      this.isFixedCompetitorPenaltyWeightDialogOpen = false;
    },

    enableFixedCompetitorPenaltyWeight() {
      const defaults = this.toggleLogic[fixedCompetitorPenaltyWeightOptions];
      // disable exact match if fixed weights are enabled
      this.selectedCompetitorPenalty.exactMatch = false;
      this.mutableRule.competitorPenalty.weighted = true;
      if (!this.mutableRule.competitorPenalty.maxPenalty) {
        this.$set(this.mutableRule.competitorPenalty, 'maxPenalty', {
          below: defaults.maxPenalty.lower,
          above: defaults.maxPenalty.upper,
        });
      }
      // weights should be equally distributed across all active competitors
      const weights = this.activeCompetitorsPenalties.reduce(
        (acc, penalty) => {
          acc.below[penalty.competitorName] = 1;
          acc.above[penalty.competitorName] = 1;
          return acc;
        },
        { below: {}, above: {} }
      );

      this.$set(this.mutableRule.competitorPenalty, 'weights', weights);
      this.mutableRule.competitorPenalty.penalties.forEach(penalty =>
        this.setWeightedPenaltyDefaults(penalty)
      );

      this.updateA2D2points();

      this.isFixedCompetitorPenaltyWeightDialogOpen = false;
    },

    setWeightedPenaltyDefaults(penalty) {
      const defaults = this.toggleLogic[fixedCompetitorPenaltyWeightOptions];

      const newLower = cloneDeep(penalty.lower);
      newLower.penaltyDeviation[0].penaltyAbsolute = undefined; // A2 - disabled - n/a
      newLower.penaltyDeviation[1].penaltyAbsolute = defaults.b2Percentage; // B2 - becomes percentage
      newLower.penaltyDeviation[1].penaltyDeviationPercent = defaults.b1Percentage; // B1

      const newUpper = cloneDeep(penalty.upper);
      newUpper.penaltyDeviation[0].penaltyAbsolute = defaults.c2Percentage; // C2 - becomes percentage
      newUpper.penaltyDeviation[0].penaltyDeviationPercent = defaults.c1Percentage; // C1
      newUpper.penaltyDeviation[1].penaltyAbsolute = undefined; // D2 - disabled - n/a

      this.$set(penalty, 'lower', newLower);
      this.$set(penalty, 'upper', newUpper);
    },

    getWeightsSum(field) {
      if (
        !this.mutableRule.competitorPenalty.weights ||
        !this.mutableRule.competitorPenalty.weights[field]
      ) {
        return 0;
      }

      return Object.values(this.mutableRule.competitorPenalty.weights[field]).reduce(
        (acc, val) => acc + Number(val),
        0
      );
    },

    isWeightedRule(item) {
      return (
        item.penalty === penaltyCategories.competitor && this.mutableRule.competitorPenalty.weighted
      );
    },

    onCompetitorActiveToggle(item) {
      if (this.isWeightedRule(item)) {
        if (!this.competitorIsActive) {
          // remove saved weights if competitor becomes inactive
          delete this.mutableRule.competitorPenalty.weights.above[
            this.selectedCompetitorPenalty.competitorName
          ];
          delete this.mutableRule.competitorPenalty.weights.below[
            this.selectedCompetitorPenalty.competitorName
          ];
        } else {
          // competitor becomes active
          // wait for distribution weights inputs to be rendered
          this.$nextTick(() => {
            // highlight in red inputs by default
            this.$refs.fixedPenaltyForm[0].validate();
          });
        }
      }
    },

    onWeightInput({ value, competitor, type }) {
      this.$set(
        this.mutableRule.competitorPenalty.weights[type],
        competitor.competitorName,
        Number(value)
      );
      this.updateA2D2points();
    },

    updateA2D2points() {
      // calculate A2, D2 competitor points for all active competitors
      this.activeCompetitorsPenalties.forEach(penalty => {
        const { maxPenalty, weights } = this.mutableRule.competitorPenalty;

        if (maxPenalty.below && weights.below[penalty.competitorName]) {
          const newLower = cloneDeep(penalty.lower);
          newLower.penaltyDeviation[0].penaltyAbsolute = Math.round(
            (maxPenalty.below * weights.below[penalty.competitorName]) / this.getWeightsSum('below')
          ); // A2
          this.$set(penalty, 'lower', newLower);
        }

        if (maxPenalty.above && weights.above[penalty.competitorName]) {
          const newUpper = cloneDeep(penalty.upper);
          newUpper.penaltyDeviation[1].penaltyAbsolute = Math.round(
            (maxPenalty.above * weights.above[penalty.competitorName]) / this.getWeightsSum('above')
          ); // D2
          this.$set(penalty, 'upper', newUpper);
        }
      });
    },

    toggleCompetitorExactMatch() {
      if (this.selectedCompetitorPenalty.exactMatch) {
        this.isCompetitorExactMatchDialogOpen = true;
        // switch off toggle, unless user confirms in modal
        this.$nextTick(() => {
          this.selectedCompetitorPenalty.exactMatch = false;
        });
      } else {
        this.$set(this.selectedCompetitorPenalty, 'exactMatch', false);
      }
    },

    closeCompetitorExactMatchDialog() {
      // Reset the checked state when user cancels the confirmation dialog
      this.selectedCompetitorPenalty.exactMatch = false;
      this.isCompetitorExactMatchDialogOpen = false;
    },

    enableCompetitorExactMatch() {
      // Enable competitor exact match and disable weighted setting
      this.selectedCompetitorPenalty.exactMatch = true;
      this.mutableRule.competitorPenalty.weighted = false;
      this.isCompetitorExactMatchDialogOpen = false;
    },

    /**
     * Checks if there's an active penalty with exact match enabled
     * @param {Object} item - The penalty item to check
     * @returns {boolean} True if the item has an active exact match penalty (either competitor or store group)
     */
    hasActiveExactMatch(item) {
      if (item.penalty === penaltyCategories.competitor) {
        return (
          this.displayCompetitorExactMatch &&
          this.mutableRule.competitorPenalty.penalties.some(
            competitor => competitor.isActive && get(competitor, 'exactMatch', false)
          )
        );
      }
      if (item.penalty === penaltyCategories.storeGroup) {
        return (
          this.displayStoreGroupExactMatch &&
          this.mutableRule.storeGroupPenalty.penalties.some(
            storeGroup => storeGroup.isActive && get(storeGroup, 'exactMatch', false)
          )
        );
      }
      return false;
    },

    /**
     * Checks if there's an active Max Up/Down feature
     * @param {Object} item - The penalty item to check
     * @returns {boolean} True if the item has an active Max Up/Down (store group only)
     */
    hasActiveMaxUpDown(item) {
      if (item.penalty === penaltyCategories.storeGroup) {
        return (
          this.displayStoreGroupMaxUpDown &&
          this.mutableRule.storeGroupPenalty.penalties.some(
            storeGroup => storeGroup.isActive && get(storeGroup, 'maxUpDown.isActive', false)
          )
        );
      }
      return false;
    },

    /**
     * Helper to check if exact match is enabled for a given penalty type
     * @param {Object} item - The penalty item to check
     * @param {Array<string>} penaltyTypes - Penalty types to check (e.g., [penaltyCategories.competitor])
     * @returns {boolean} True if exact match is enabled for the specified penalty type(s)
     */
    isExactMatchEnabledForPenalty(item, penaltyTypes) {
      if (
        penaltyTypes.includes(penaltyCategories.competitor) &&
        item.penalty === penaltyCategories.competitor
      ) {
        return this.displayCompetitorExactMatch && this.competitorIsExactMatch;
      }
      if (
        penaltyTypes.includes(penaltyCategories.storeGroup) &&
        item.penalty === penaltyCategories.storeGroup
      ) {
        return this.displayStoreGroupExactMatch && this.storeGroupIsExactMatch;
      }
      return false;
    },

    /**
     * Determines if exact match should disable offset inputs (only for competitor exact match)
     * @param {Object} item - The penalty item to check
     * @returns {boolean} True if offset fields should be disabled
     */
    isExactMatchDisablingOffsets(item) {
      return this.isExactMatchEnabledForPenalty(item, [penaltyCategories.competitor]);
    },

    /**
     * Determines if exact match should disable chart and parameters (both competitor and store group)
     * @param {Object} item - The penalty item to check
     * @returns {boolean} True if chart and parameters should be disabled and opaque
     */
    isExactMatchDisablingChartAndParameters(item) {
      return this.isExactMatchEnabledForPenalty(item, [
        penaltyCategories.competitor,
        penaltyCategories.storeGroup,
      ]);
    },

    /**
     * Generic method to determine if chart and parameters should be disabled
     * Accounts for exact match and store group Max Up/Down feature
     * @param {Object} item - The penalty item to check
     * @returns {boolean} True if chart and parameters should be disabled and opaque
     */
    shouldDisableChartAndParameters(item) {
      return (
        this.isExactMatchDisablingChartAndParameters(item) ||
        (item.penalty === penaltyCategories.storeGroup && this.isStoreGroupMaxUpDownEnabled)
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.rule-content {
  height: 100%;
  overflow: scroll;

  &::v-deep .v-label {
    font-size: 1.2rem;
  }

  .formWrapper {
    background-color: #e7e7e7;
    padding: 1.5rem;

    &--weighted {
      background-color: $fixedCompetitorsPenaltyWeightContainerColor;
    }

    &::v-deep .v-label {
      top: 0;
    }

    &::v-deep input {
      padding: 0.3rem;
    }

    &::v-deep .v-select__selection--comma {
      margin: 0;
      padding: 0.3rem;
    }

    &::v-deep .v-select__slot {
      height: 2.6rem;
    }
  }

  .fixed-competitor {
    &__form {
      height: 100%;
      background-color: $fixedCompetitorsPenaltyWeightContainerColor;
      font-size: 12px;

      .disabled-text {
        color: $fixedCompetitorsPenaltyWeightTextDisabledColor;
      }

      &::v-deep input {
        padding: 0.3rem;
      }
    }

    &__penalty-table-container {
      max-height: 213px;
      overflow: auto;
    }

    &__penalty-table {
      width: 100%;
      background-color: $pricing-white;
      border-spacing: 0;

      &--narrow {
        width: 60%;
        td {
          width: 90px;
        }
      }

      th {
        border-bottom: 1px solid $fixedCompetitorsPenaltyWeightTableBorderColor;
        font-weight: normal;
        padding: 4px 8px;
      }

      tr {
        height: 32px;
        text-align: left;

        &:nth-child(even) {
          background-color: $fixedCompetitorsPenaltyWeightTableEvenRowColor;
        }

        td {
          padding: 4px 8px;

          &.input-column {
            width: 90px;

            .pricing-tooltip {
              justify-content: end;

              &::v-deep .tooltip-container {
                top: -35px;
              }
            }
          }
        }
      }
    }
  }

  .error-text {
    color: $vuetify-error-red;
    font-size: 1.2rem;
  }

  .penalty-form {
    min-width: 15rem;
  }
  .chart-container {
    min-width: 57rem;
  }

  .activate-checkbox {
    margin-top: 2rem;
    justify-content: flex-end;
  }

  .tab-title {
    margin-left: 0.5rem;
    margin-top: 0.5rem;
  }

  .headline {
    font-size: 1.5rem !important;
  }

  .traffic-light {
    margin-right: 0.5rem;
  }

  .economic-penalty-traffic-light {
    background-color: $economicTrafficLightColor;
  }

  .competitor-penalty-traffic-light {
    background-color: $competitorTrafficLightColor;
  }

  .storeGroup-penalty-traffic-light {
    background-color: $storeGroupTrafficLightColor;
  }

  .architecture-penalty-traffic-light {
    background-color: $architectureTrafficLightColor;
  }

  .minMax-penalty-traffic-light {
    background-color: $minMaxTrafficLightColor;
  }

  .pricePointing-penalty-traffic-light {
    background-color: $pricePointingTrafficLightColor;
  }

  .maxUpDown-penalty-traffic-light {
    background-color: $maxUpDownTrafficLightColor;
  }

  .other-penalty-traffic-light {
    background-color: $otherTrafficLightColor;
  }

  .mud-row {
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 0.5rem;
  }

  .mud-control {
    flex: 0 0 auto;
    display: flex;
    margin-top: 1.4rem;
  }

  .mud-input {
    flex: 0 0 auto;
    min-width: 12rem;
  }

  .store-group-offsets-panel {
    flex: 0 0 auto;
    min-width: 14rem;
  }

  .panels-form {
    display: flex;
    flex-direction: column;
  }

  .panels-stretch {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .panels-row {
    flex: 1;
    height: 100%;
    gap: 0.75rem;
    align-items: stretch;
  }

  .offset-panel-box {
    background-color: $paramsFormBackgroundColor;
    padding: 1.5rem;
    align-self: stretch;
  }

  .tab-content {
    font-size: 1.6rem;
  }

  .btn-block {
    height: calc(100% - 11rem);
  }

  .prefix,
  .suffix {
    font-size: 1.3rem;
    padding: 0;
  }

  .prefix {
    padding-right: 0.5rem;
  }

  .suffix {
    padding-left: 0.5rem;
  }

  .field-heading {
    font-size: 1.2rem !important;
  }

  .v-input--checkbox {
    font-size: 1.2rem;
  }

  .max-up-down-input {
    &::v-deep .v-input__control {
      min-height: 32px;
    }

    &::v-deep .v-input__slot {
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  .error-message {
    font-size: 1.2rem;
    margin-right: 0.5rem;
  }

  .offset-type-control {
    width: 100%;
  }

  .offset-type-label {
    font-size: 0.95rem;
    color: #333;
    min-width: 70px;
  }
}

.expanded-bottom {
  box-shadow: 0 0.3rem 0.5rem -0.1rem rgba(0, 0, 0, 0.2), 0 0.6rem 1rem 0 rgba(0, 0, 0, 0.14);
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.rule-card {
  padding: 1rem 1rem 1rem 1rem;
  transition: none;

  &.expanded-top {
    margin-bottom: 0;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}

.card-text {
  font-size: 1.25rem;
}

.rule-content {
  padding-bottom: 1rem;
}

.add-pricepointing-text {
  font-size: 1.25rem;
  margin-left: 1rem;
}

.add-pricepointing-container {
  display: flex;
  align-items: center;
  flex: 1 0 0;
}

.add-pricepointing-icon {
  color: $rulesetDarkBlue;
  padding-left: 0;
}

.opaque {
  opacity: 0.5;
}

.weighted-penalty-warning {
  font-size: 1.2rem;
}
</style>
